import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Product } from '../../services/product';
import { ProductProcessingModal } from './ProductProcessingModal';
import { Eye, Image, Package, Database, CheckCircle2, Clock } from 'lucide-react';

interface ProductListItemProps {
  product: Product;
}

export function ProductListItem({ product }: ProductListItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const hasImages = !!product.main_image_url || (product.gallery_images && product.gallery_images.length > 0);

  // Count unique images to avoid double-counting main image
  const imageCount = React.useMemo(() => {
    if (!product.gallery_images || product.gallery_images.length === 0) {
      return product.main_image_url ? 1 : 0;
    }

    // Extract unique image URLs from gallery_images
    const uniqueUrls = new Set<string>();
    product.gallery_images.forEach((img: any) => {
      const url = typeof img === 'string' ? img : img?.url;
      if (url) {
        uniqueUrls.add(url);
      }
    });

    return uniqueUrls.size;
  }, [product.gallery_images, product.main_image_url]);

  const handleViewProduct = () => {
    // Navigate to product detail and preserve current location in state
    navigate(`/products/${product.id}`, {
      state: { 
        from: location.pathname + location.search,
        returnTo: '/products' + location.search 
      }
    });
  };

  const handleProcessProduct = () => {
    setIsProcessingModalOpen(true);
  };

  const handleJobCreated = (jobId: string, isAsync?: boolean) => {
    setIsProcessingModalOpen(false);
    // You can add toast notifications here if needed
    console.log(`Job created: ${jobId}, async: ${isAsync}`);
  };

  return (
    <Card className="card-modern-enhanced group hover:shadow-xl hover:border-primary/40 transition-all duration-300 h-56 overflow-hidden">
      <CardContent className="p-0 h-full">
        <div className="flex items-stretch h-full">

          {/* Product image - fills full height */}
          <div className="relative w-48 bg-gradient-to-br from-muted/50 to-muted flex-shrink-0 overflow-hidden">
            {product.main_image_url ? (
              <img
                src={product.main_image_url}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <Package className="h-16 w-16" />
              </div>
            )}
            
            {/* Modern overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Image count badge */}
            {hasImages && (
              <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full flex items-center shadow-lg font-medium">
                <Image className="h-4 w-4 mr-1.5" />
                {imageCount}
              </div>
            )}
          </div>

          {/* Product info - clean and spacious */}
          <div className="flex-1 p-6 flex flex-col justify-between h-full">
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold leading-tight line-clamp-1 mb-1">
                      {product.name}
                    </h3>
                    <p className="text-sm font-medium text-muted-foreground">
                      SKU: {product.sku}
                    </p>
                  </div>
                  
                  <div className="space-y-1.5">
                    {product.stores && (
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                        <span className="font-medium">Store:</span> {product.stores.name}
                      </div>
                    )}

                    {product.last_synced && (
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-success rounded-full"></div>
                        <span className="font-medium">Last synced:</span> {new Date(product.last_synced).toLocaleDateString()}
                      </div>
                    )}

                    {(product.stock_quantity !== null && product.stock_quantity !== undefined) && (
                      <div className="text-sm flex items-center gap-2">
                        <Database className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium text-muted-foreground">Stock:</span>
                        <span className="font-semibold text-foreground">{product.stock_quantity}</span>
                        <Badge
                          variant={product.stock_quantity > 0 ? 'default' : 'secondary'}
                          className={`text-[10px] h-5 ml-1 ${
                            product.stock_quantity > 0
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {product.stock_quantity > 0 ? 'In Stock' : 'Out of Stock'}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions - top right corner */}
                <div className="flex items-center gap-2 ml-6">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleViewProduct}
                    className="px-4 hover:bg-muted/50"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button 
                    size="sm"
                    disabled={!hasImages}
                    className="px-4 shadow-md"
                    onClick={handleProcessProduct}
                  >
                    Process
                  </Button>
                </div>
              </div>

              {/* Modern badge layout */}
              <div className="flex items-center gap-2 flex-wrap">
                {product.status && (
                  product.status === 'enabled' ? (
                    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-gradient-to-r from-success to-success/80 text-success-foreground shadow-md">
                      active
                    </span>
                  ) : (
                    <Badge variant="secondary" className="text-xs shadow-sm">
                      {product.status}
                    </Badge>
                  )
                )}
                {/* Transformation status badge */}
                {product.transformation_status && product.transformation_status !== 'untransformed' && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold shadow-md ${
                    product.transformation_status === 'complete'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                      : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                  }`}>
                    {product.transformation_status === 'complete' ? (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    ) : (
                      <Clock className="h-3 w-3 mr-1" />
                    )}
                    {product.transformation_status === 'complete' ? 'Transformed' : 'Partial'}
                  </span>
                )}
                {product.category && (
                  <>
                    {product.category.split(',').slice(0, 3).map((cat, index) => (
                      <Badge key={index} variant="outline" className="text-xs font-medium">
                        {cat.trim()}
                      </Badge>
                    ))}
                    {product.category.split(',').length > 3 && (
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        +{product.category.split(',').length - 3}
                      </Badge>
                    )}
                  </>
                )}
                {!hasImages && (
                  <Badge variant="outline" className="text-xs text-warning border-warning/20 bg-warning/10">
                    No images
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      
      {/* Processing Modal */}
      <ProductProcessingModal
        product={product}
        isOpen={isProcessingModalOpen}
        onClose={() => setIsProcessingModalOpen(false)}
        onJobCreated={handleJobCreated}
      />
    </Card>
  );
}